// src/pages/ContactsPage.jsx
import { useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { CONTACTS } from '../config/contacts.js';
import AOS from 'aos';
import 'aos/dist/aos.css';
import ContactFormTabs from '../components/ContactFormTabs';
import SeoMeta from '../components/SeoMeta';
import { usePageMeta } from '../hooks/usePageMeta';

// Leaflet карта
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

/* =================== animations =================== */
const float = keyframes`
  0%   { transform: translateY(0) }
  50%  { transform: translateY(-6px) }
  100% { transform: translateY(0) }
`;
const pulse = keyframes`
  0% { transform: scale(1); opacity: .7 }
  50% { transform: scale(1.03); opacity: 1 }
  100% { transform: scale(1); opacity: .7 }
`;

/* =================== layout =================== */
const Wrap = styled.div``;

const Hero = styled.section`
  position: relative; overflow: hidden;
  padding: clamp(56px, 8vw, 104px) 0 28px;
  .bg {
    position: absolute; inset: -20%;
    background:
      radial-gradient(40% 40% at 15% 10%, #ff660026, transparent 60%),
      radial-gradient(35% 35% at 80% 5%, #00c2ff22, transparent 60%),
      radial-gradient(50% 50% at 50% 120%, #9bff0024, transparent 60%);
    filter: blur(40px);
    transform: translateZ(0);
  }
  .inner { position:relative; width:${({theme})=>theme.container}; margin:0 auto; }
  h1 { font-size: ${({theme})=>theme.font.sizes.h1}; margin:0 0 8px; }
  p.lead { color:${({theme})=>theme.textMuted}; margin:0; max-width:800px; }
`;

const StatusBar = styled.div`
  margin-top: 16px;
  display: grid; grid-template-columns: 1fr; gap: 10px;
  padding: 12px; border:1px dashed ${({theme})=>theme.border};
  background: ${({theme})=>theme.surface}; border-radius:${({theme})=>theme.radius.lg}px;

  .row { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
  .seg {
    display:inline-flex; gap:6px; align-items:center; padding:6px 10px;
    border:1px solid ${({theme})=>theme.border}; border-radius:999px;
    background:${({theme})=>theme.body}; cursor:pointer; user-select:none;
  }
  .seg[aria-pressed="true"]{
    outline:2px solid ${({theme})=>theme.accent};
    box-shadow: ${({theme})=>theme.shadow.sm};
  }
  .dot {
    width:8px; height:8px; border-radius:50%;
    background: var(--ok, #23c552); box-shadow: 0 0 0 4px rgba(35,197,82,.12);
    animation:${pulse} 2.8s ease-in-out infinite;
  }
  .timer { color:${({theme})=>theme.textMuted}; }
`;

const Section = styled.section`
  padding: 56px 0;
  &.muted { background: ${({theme})=>theme.body}; }
  .inner { width:${({theme})=>theme.container}; margin:0 auto; }
  .head { display:flex; align-items:end; justify-content:space-between; gap:16px; margin-bottom:20px; }
  .kicker { font-size:13px; letter-spacing:.14em; text-transform:uppercase; opacity:.7; }
`;

const Grid = styled.div`
  display:grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: 16px;
  @media (max-width: 1100px){ grid-template-columns: repeat(2, minmax(0,1fr)); }
  @media (max-width: 720px){ grid-template-columns: 1fr; }
`;

const DeptCard = styled.article`
  position:relative; overflow:hidden;
  border:1px solid ${({theme})=>theme.border};
  background:${({theme})=>theme.surface};
  border-radius:${({theme})=>theme.radius.xl}px;
  box-shadow:${({theme})=>theme.shadow.sm};

  .media{
    position:relative; height:160px; overflow:hidden;
    img{ width:100%; height:100%; object-fit:cover; display:block; transform:scale(1.04); transition: transform .6s ease; }
    &:after{
      content:''; position:absolute; inset:0;
      background: linear-gradient(to top, rgba(0,0,0,.35), rgba(0,0,0,0) 60%);
    }
  }
  &:hover .media img{ transform:scale(1.08); }

  .cap{
    display:grid; gap:8px; padding:14px 16px;
  }
  .title{
    display:flex; align-items:center; gap:10px;
    b{ font-size:16px; }
    .icon{
      width:38px; height:38px; border-radius:12px; display:grid; place-items:center;
      border:1px solid ${({theme})=>theme.border}; background:${({theme})=>theme.body};
      animation:${float} 5s ease-in-out infinite;
    }
  }
  .row{ display:flex; flex-wrap:wrap; gap:8px; }
  a.tag, span.tag{
    font-size:13px; text-decoration:none; padding:6px 10px; border-radius:${({theme})=>theme.radius.pill}px;
    border:1px solid ${({theme})=>theme.border}; background:${({theme})=>theme.body}; color:inherit; display:inline-flex; align-items:center; gap:6px;
  }
  .note{ font-size:14px; color:${({theme})=>theme.textMuted}; }
`;

/* ====== Who is your manager ====== */
const ManagerWrap = styled.div`
  border:1px solid ${({theme})=>theme.border};
  background:${({theme})=>theme.surface};
  border-radius:${({theme})=>theme.radius.lg}px; padding:16px;
  display:grid; gap:12px; box-shadow:${({theme})=>theme.shadow.sm};

  .row{ display:flex; gap:12px; align-items:center; flex-wrap:wrap; }
  select{
    border:1px solid ${({theme})=>theme.border};
    background:${({theme})=>theme.body};
    padding:8px 10px; border-radius:${({theme})=>theme.radius.md}px; color:inherit;
  }
`;

const ManagerCard = styled.div`
  display:flex; gap:12px; align-items:center; padding:12px;
  border:1px dashed ${({theme})=>theme.border}; border-radius:${({theme})=>theme.radius.lg}px;
  background:${({theme})=>theme.body};
  .avatar{
    width:56px; height:56px; border-radius:50%; overflow:hidden;
    border:1px solid ${({theme})=>theme.border}; background:${({theme})=>theme.surface};
  }
  .meta{ line-height:1.25; }
  .meta b{ display:block; }
  .meta small{ color:${({theme})=>theme.textMuted}; }
  .links{ margin-left:auto; display:flex; gap:8px; flex-wrap:wrap; }
  .chip{
    padding:6px 10px; border-radius:999px; border:1px solid ${({theme})=>theme.border};
    background:${({theme})=>theme.surface}; font-size:13px; text-decoration:none; color:inherit;
  }
`;

/* ====== Map ====== */
const TwoCols = styled.div`
  display:grid; grid-template-columns: 1fr; gap: 16px;
`;
const MapWrap = styled.div`
  border:1px solid ${({theme})=>theme.border};
  border-radius:${({theme})=>theme.radius.xl}px; overflow:hidden;
  background:${({theme})=>theme.surface};
  box-shadow:${({theme})=>theme.shadow.sm};
  .map{ height: 460px; }
  .legend{ border-top:1px solid ${({theme})=>theme.border}; padding: 10px 12px; display:flex; flex-wrap:wrap; gap:10px; }
  .pill{ font-size:13px; padding:6px 10px; border-radius:${({theme})=>theme.radius.pill}px; border:1px dashed ${({theme})=>theme.border}; }
`;

/* ====== Contact form helper rows ====== */
const ChipsRow = styled.div`
  display:flex; gap:8px; flex-wrap:wrap; margin-top:10px;
  a.chip{
    display:inline-flex; align-items:center; gap:8px; text-decoration:none; color:inherit;
    border:1px solid ${({theme})=>theme.border}; background:${({theme})=>theme.body};
    border-radius:999px; padding:8px 12px; font-weight:600;
  }
  .tg { color:#229ED9 }
  .vb { color:#7360F2 }
  .wa { color:#25D366 }
`;
const Badge = styled.div`
  margin-top:10px; font-size:13px; border:1px dashed ${({theme})=>theme.border};
  background:${({theme})=>theme.surface}; padding:8px 10px; border-radius:${({theme})=>theme.radius.md}px;
`;
const LoadBar = styled.div`
  margin-top:10px; display:grid; gap:6px;
  .bar{ height:10px; background:${({theme})=>theme.body}; border:1px solid ${({theme})=>theme.border}; border-radius:999px; overflow:hidden; }
  .fill{ height:100%; background: linear-gradient(90deg, #ff6600, #ff9a3c); width: var(--val, 65%); }
`;

/* =================== local SVG icons =================== */
const Icon = ({ name, size=20 }) => {
  const path = {
    buy:  "M3 6h2l2 10h10l2-8H7",
    sale: "M3 12l4-4h6l4 4-4 4H7l-4-4z",
    store:"M4 10h16v10H4V10zm2-6h12l2 4H4l2-4z",
    wood: "M12 2l8 6-8 6-8-6 8-6zm0 8l8 6-8 6-8-6 8-6z",
    metal:"M3 7h18v4H3V7zm0 6h18v4H3v-4z",
    law:  "M12 3l7 4v3H5V7l7-4zm-7 9h14v9H5v-9z",
    book: "M6 4h9a3 3 0 013 3v13a3 3 0 00-3-3H6V4zM6 17h9",
    phone:"M6.62 10.79a15 15 0 006.59 6.59l2.2-2.2c.3-.3.74-.4 1.12-.27A12 12 0 0020 16v3a1 1 0 01-1 1C10.07 20 4 13.93 4 6a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.46.57 3.58a1 1 0 01-.24 1.01l-2.2 2.2z",
    mail: "M4 6h16a2 2 0 012 2v.4L12 14 2 8.4V8a2 2 0 012-2zm0 4.45L12.53 16a1 1 0 001.06 0L20 10.45V18a2 2 0 01-2 2H6a2 2 0 01-2-2v-7.55z",
    pin:  "M12 2a7 7 0 00-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 00-7-7zm0 9a2 2 0 110-4 2 2 0 010 4z",
    tg:   "M21.94 2.34a1 1 0 00-1.06-.15L2.64 10.5a1 1 0 00.08 1.86l5.22 1.85 1.84 5.22a1 1 0 001.86.08l8.31-18.24a1 1 0 00-.01-.93zM9.83 16.34l-1.15-3.25 7.5-5.51-6.35 6.94-3.15-1.12 12.59-6.03L9.83 16.34z",
    vb:   "M6 2h12v20H6zM8 4h8v16H8z",
    wa:   "M5 19l1.2-3.8A8 8 0 1120 12a8 8 0 01-12.6 6.4L5 19zm7-11a5 5 0 100 10 5 5 0 000-10z",
  }[name] || "M12 2l8 6-8 6-8-6 8-6z";
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d={path}/></svg>;
};

/* =================== data =================== */
const DEPTS = [
  { key:'purchase',   title:'Відділ закупівель',          img:'/img/contacts/purchase.jpg',   phones:['+380 (67) 000 00 01'], email:'purchase@momv.com.ua',   tags:['Сировина','HPL','Комплектуючі'], note:'Працюємо з постачальниками України та ЄС.', icon:'buy' },
  { key:'sales',      title:'Відділ продажу',             img:'/img/contacts/sales.jpg',      phones:['+380 (67) 000 00 02'], email:'sales@momv.com.ua',      tags:['Комерційні пропозиції','Договір','Проєкти'], note:'Розрахунок вартості за кресленнями та ТЗ.', icon:'sale' },
  { key:'warehouse',  title:'Склад / Логістика',          img:'/img/contacts/warehouse.jpg',  phones:['+380 (67) 000 00 03'], email:'logistics@momv.com.ua',  tags:['Відвантаження','Палетування','ТТН'], note:'Відправка по Україні, ЄС — за запитом.', icon:'store' },
  { key:'mebli',      title:'Виробництво меблів (Бровари)', img:'/img/contacts/mebli.jpg',   phones:['+380 (67) 000 00 04'], email:'mebli@momv.com.ua',      tags:['Проєктування','Розкрій','Монтаж'], note:'Серійні та індивідуальні рішення.', icon:'wood' },
  { key:'metal',      title:'Металообробка (Черкаси)',    img:'/img/contacts/metal.jpg',      phones:['+380 (67) 000 00 05'], email:'metal@momv.com.ua',      tags:['Токарні/фрезерні','Зварювання','Термообробка'], note:'Вали до 6 м, зубонарізання, порошкове фарбування.', icon:'metal' },
  { key:'accounting', title:'Бухгалтерія',                img:'/img/contacts/accounting.jpg', phones:['+380 (67) 000 00 06'], email:'accounting@momv.com.ua', tags:['Закриття періоду','Рахунки','Акти'], note:'Первинні документи та звітність.', icon:'book' },
  { key:'legal',      title:'Юридичний відділ',           img:'/img/contacts/legal.jpg',      phones:['+380 (67) 000 00 07'], email:'legal@momv.com.ua',      tags:['Договори','Аудит','IP'], note:'Перевірка контрагентів, узгодження умов.', icon:'law' },
];

const MANAGERS = {
  sales:     { name:'Олександр Романенко', role:'Керівник продажів', avatar:'/img/people/sales1.jpg',  email:'sales@momv.com.ua',  phone:'+380670000002' },
  purchase:  { name:'Марія Коваль',        role:'Категорійний менеджер', avatar:'/img/people/purchase1.jpg', email:'purchase@momv.com.ua', phone:'+380670000001' },
  mebli:     { name:'Ірина Бровко',        role:'Проєкт-менеджер (меблі)', avatar:'/img/people/mebli1.jpg', email:'mebli@momv.com.ua', phone:'+380670000004' },
  metal:     { name:'Денис Черкаський',     role:'Тех. менеджер (метал)', avatar:'/img/people/metal1.jpg', email:'metal@momv.com.ua', phone:'+380670000005' },
  warehouse: { name:'Максим Логіст',       role:'Логіст', avatar:'/img/people/warehouse1.jpg', email:'logistics@momv.com.ua', phone:'+380670000003' },
};

const LOCATIONS = [
  { id: 'kyiv',     name: 'Київ — офіс',                  addr:'б-р Незалежності, 1', pos:[50.4501, 30.5234] },
  { id: 'brovary',  name: 'Бровари — виробництво меблів', addr:'вул. Промислова, 5',  pos:[50.5111, 30.7909] },
  { id: 'cherkasy', name:'Черкаси — металлообробка',      addr:'вул. Заводська, 10',  pos:[49.4444, 32.0598] },
];

// Графік роботи (локально у браузері користувача)
const SCHEDULE = {
  office: {
    Mon:{start:'10:00', end:'18:00'}, Tue:{start:'10:00', end:'18:00'}, Wed:{start:'10:00', end:'18:00'}, Thu:{start:'10:00', end:'18:00'},
    Fri:{start:'10:00', end:'17:00'}, Sat:null, Sun:null,
  },
  warehouse: {
    Mon:{start:'10:00', end:'18:00'}, Tue:{start:'10:00', end:'18:00'}, Wed:{start:'10:00', end:'18:00'}, Thu:{start:'10:00', end:'18:00'},
    Fri:{start:'10:00', end:'17:00'}, Sat:{start:'09:00', end:'15:00'}, Sun:null,
  },
  production: {
    Mon:{start:'10:00', end:'18:00'}, Tue:{start:'10:00', end:'18:00'}, Wed:{start:'10:00', end:'18:00'}, Thu:{start:'10:00', end:'18:00'},
    Fri:{start:'10:00', end:'17:00'}, Sat:{start:'09:00', end:'18:00'}, Sun:null,
  }
};

// Leaflet маркер
const markerIcon = new L.Icon({
  iconUrl: '/pin.svg',
  iconRetinaUrl: '/pin.svg',
  iconSize: [34, 34],
  iconAnchor: [17, 32],
  popupAnchor: [0, -26],
});

/* =================== helpers =================== */
const wday = (d)=>(['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d.getDay()]);
function parseHM(hm){ const [h,m]=hm.split(':').map(Number); return {h,m}; }
function setHM(date, hm){
  const d = new Date(date);
  const {h,m} = parseHM(hm);
  d.setHours(h, m, 0, 0);
  return d;
}
function fmtHM(date){ return date.toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'}); }
function humanDiff(ms){
  if (ms<=0) return '0 хв';
  const m = Math.floor(ms/60000);
  const h = Math.floor(m/60);
  const mm = m%60;
  return h ? `${h} год ${mm} хв` : `${mm} хв`;
}

/* =================== main =================== */
export default function ContactsPage(){
  const meta = usePageMeta('contacts');
  const { email } = CONTACTS;
  const [activeLoc, setActiveLoc] = useState(LOCATIONS[0].id);
  const activePos = useMemo(()=> (LOCATIONS.find(l=>l.id===activeLoc)?.pos || LOCATIONS[0].pos), [activeLoc]);

  // статусний віджет
  const [unit, setUnit] = useState('office'); // office | warehouse | production
  const [statusText, setStatusText] = useState('');

  useEffect(()=>{
    AOS.init({ duration: 700, easing:'ease-out-cubic', offset:60, once:true });
  },[]);

  useEffect(()=>{
    const tick = ()=>{
      const now = new Date();
      const wd = wday(now);
      const plan = SCHEDULE[unit][wd];
      if(!plan){ setStatusText('Сьогодні вихідний'); return; }
      const start = setHM(now, plan.start);
      const end = setHM(now, plan.end);
      const open = now >= start && now <= end;
      const left = end - now;
      setStatusText(
        open
          ? `Працюємо до ${fmtHM(end)} • Залишилося ~ ${humanDiff(left)}`
          : (now < start ? `Відкриваємось о ${fmtHM(start)}` : 'Вже зачинено')
      );
    };
    tick();
    const id = setInterval(tick, 30_000);
    return ()=>clearInterval(id);
  },[unit]);

  // менеджер за напрямом
  const [deptKey, setDeptKey] = useState('sales');
  const mgr = MANAGERS[deptKey];

  useEffect(()=>{ /* no-op for AOS refresh on tab changes */ }, [deptKey, unit]);

  return (
    <Wrap>
      <SeoMeta
        title={meta.title}
        description={meta.description}
        keywords={meta.keywords}
        image={meta.image}
        path={meta.path || '/contacts'}
      />
      {/* HERO */}
      <Hero>
        <div className="bg" aria-hidden />
        <div className="inner">
          <h1 data-aos="fade-up">Контакти MOMV</h1>
          <p className="lead" data-aos="fade-up" data-aos-delay="80">
            Оберіть відділ або напишіть нам — відповімо оперативно й по суті.
          </p>
          <ChipsRow>
            <a className="chip tg" href="https://t.me/momv" target="_blank" rel="noreferrer"><Icon name="tg" /> Telegram</a>
            <a className="chip vb" href="viber://chat?number=%2B380670000001"><Icon name="vb" /> Viber</a>
            <a className="chip wa" href="https://wa.me/380670000001" target="_blank" rel="noreferrer"><Icon name="wa" /> WhatsApp</a>
          </ChipsRow>

          {/* статус + перемикач юнітів */}
          <StatusBar data-aos="fade-up" data-aos-delay="120">
            <div className="row" role="tablist" aria-label="Робочі підрозділи">
              {[
                {id:'office', label:'Офіс'},
                {id:'warehouse', label:'Склад'},
                {id:'production', label:'Виробництво'},
              ].map(x=>(
                <button key={x.id} className="seg" role="tab"
                        aria-pressed={unit===x.id}
                        onClick={()=>setUnit(x.id)}>
                  <span className="dot" />
                  {x.label}
                </button>
              ))}
              <span className="timer">Сьогодні: {statusText}</span>
            </div>
          </StatusBar>
        </div>
      </Hero>

      {/* Хто ваш контакт */}
      <Section>
        <div className="inner">
          <div className="head">
            <div>
              <div className="kicker">Довіра</div>
              <h2>Хто ваш контакт?</h2>
            </div>
          </div>

          <ManagerWrap data-aos="fade-up">
            <div className="row">
              <label htmlFor="deptSel"><b>Напрям:</b></label>
              <select id="deptSel" value={deptKey} onChange={e=>setDeptKey(e.target.value)}>
                <option value="sales">Продажі / Комерційні</option>
                <option value="purchase">Закупівлі</option>
                <option value="mebli">Меблі</option>
                <option value="metal">Металообробка</option>
                <option value="warehouse">Логістика</option>
              </select>
            </div>

            <ManagerCard>
              <div className="avatar">
                <img src={mgr.avatar} alt="" width="56" height="56" style={{width:'100%',height:'100%',objectFit:'cover'}} />
              </div>
              <div className="meta">
                <b>{mgr.name}</b>
                <small>{mgr.role}</small>
              </div>
              <div className="links">
                <a className="chip" href={`mailto:${mgr.email}`}><Icon name="mail" /> {mgr.email}</a>
                <a className="chip" href={`tel:${mgr.phone}`}><Icon name="phone" /> {mgr.phone}</a>
              </div>
            </ManagerCard>
          </ManagerWrap>
        </div>
      </Section>

      {/* Відділи та підрозділи */}
      <Section className="muted">
        <div className="inner">
          <div className="head">
            <div>
              <div className="kicker">Комунікації</div>
              <h2>Відділи та підрозділи</h2>
            </div>
          </div>

          <Grid>
            {DEPTS.map((d, i)=>(
              <DeptCard key={d.key} data-aos="fade-up" data-aos-delay={i*50}>
                <div className="media" aria-hidden>
                  <img src={d.img} alt="" loading="lazy" />
                </div>
                <div className="cap">
                  <div className="title">
                    <span className="icon"><Icon name={d.icon} /></span>
                    <b>{d.title}</b>
                  </div>
                  <div className="row">
                    {d.phones.map((p, idx)=>(
                      <a key={idx} className="tag" href={`tel:${p.replace(/[^\d+]/g,'')}`}><Icon name="phone" /> {p}</a>
                    ))}
                    {d.email && <a className="tag" href={`mailto:${d.email}`}><Icon name="mail" /> {d.email}</a>}
                  </div>
                  {!!d.tags?.length && (
                    <div className="row" aria-label="Номенклатура">
                      {d.tags.map((t, idx)=>(<span key={idx} className="tag">{t}</span>))}
                    </div>
                  )}
                  {d.note && <div className="note">{d.note}</div>}
                </div>
              </DeptCard>
            ))}
          </Grid>
        </div>
      </Section>

      {/* Зворотній звʼязок + месенджери/SLA/завантаження */}
      <section style={{padding: '24px 0'}}>
        <div style={{width: 'var(--container, 500px)', margin: '0 auto'}}>
          <h2 style={{marginBottom: 12}}>Зворотній звʼязок</h2>
          <ContactFormTabs defaultTab="sales" />

          <Badge>📮 SLA: Відповідаємо в робочі години ≤ 1 год, у позаробочий час — на ранок наступного дня.</Badge>

          <LoadBar>
            <div style={{display:'flex', justifyContent:'space-between'}}>
              <span>Завантаження цеху на тиждень</span>
              <b style={{color:'var(--accent, #ff6600)'}}>~65%</b>
            </div>
            <div className="bar"><div className="fill" style={{'--val':'65%'}} /></div>
          </LoadBar>
        </div>
      </section>

      {/* Карта (3 адреси) */}
      <Section>
        <div className="inner">
          <div className="head" style={{marginBottom:12}}>
            <div>
              <div className="kicker">Локації</div>
              <h2>Офіс та виробництва</h2>
            </div>
          </div>

          <div style={{display:'flex', gap:8, flexWrap:'wrap', marginBottom:12}}>
            {LOCATIONS.map(l=>(
              <button
                key={l.id}
                className="btn"
                onClick={()=>setActiveLoc(l.id)}
                aria-pressed={activeLoc===l.id}
                style={{
                  padding:'8px 12px',
                  borderRadius:'999px',
                  border:'1px solid var(--border, #e4e6eb)',
                  background: activeLoc===l.id ? 'var(--surface)' : 'var(--body)',
                  cursor:'pointer'
                }}
              >
                <Icon name="pin" /> {l.name}
              </button>
            ))}
          </div>

          <TwoCols>
            <MapWrap data-aos="zoom-in">
              <div className="map">
                <MapContainer
                  center={activePos}
                  zoom={7}
                  scrollWheelZoom={false}
                  style={{height:'100%', width:'100%'}}
                  key={activeLoc}
                >
                  <TileLayer
                    attribution='&copy; OpenStreetMap'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  {LOCATIONS.map(p=>(
                    <Marker key={p.id} position={p.pos} icon={markerIcon}>
                      <Popup><b>{p.name}</b><br/>{p.addr}</Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </div>
              <div className="legend">
                {LOCATIONS.map(p=>(
                  <span className="pill" key={p.id}><Icon name="pin" /> {p.name}: {p.addr}</span>
                ))}
              </div>
            </MapWrap>
          </TwoCols>
        </div>
      </Section>

      {/* Реквізити + графік роботи */}
      <Section className="muted">
        <div className="inner" style={{display:'grid', gap:16}}>
          <div className="head"><div><div className="kicker">Реквізити</div><h2>Юридична інформація</h2></div></div>
          <div style={{
            display:'grid',
            gridTemplateColumns:'repeat(2, minmax(0,1fr))',
            gap:12
          }}>
            <div style={{border:'1px solid var(--border)', borderRadius:12, padding:12, background:'var(--surface)'}}>
              <b>MOMV LLC</b><br/>
              ЄДРПОУ: 12345678<br/>
              IBAN: UA00 1234 5678 9012 3456 7890 1234<br/>
              ПДВ-статус: платник ПДВ
            </div>
            <div style={{border:'1px solid var(--border)', borderRadius:12, padding:12, background:'var(--surface)'}}>
              <b>Графік роботи</b><br/>
              Пн–Чт: 10:00 — 18:00<br/>
              Пт: 10:00 — 17:00<br/>
              Сб: офіс — вихідний; склад — 09:00–15:00; виробництва — 09:00–18:00<br/>
              Нд: вихідний
            </div>
          </div>
        </div>
      </Section>
    </Wrap>
  );
}
